import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DateInput } from '../components/DateInput';
import { EditIcon, GripIcon, TrashIcon } from '../components/Icons';
import { fmt, fmtDate, todayIso } from '../lib/format';

type Props = {
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type MovementType = 'gasto' | 'cobro';

type Movement = {
  id: string;
  date: string;
  type: MovementType;
  amount: string;
  concept: string | null;
};

type PersonalResponse = {
  items: Movement[];
  openingBalance: number;
  totals: { cobros: number; gastos: number; balance: number };
};

export function Personal({ onToast, onError }: Props) {
  const [items, setItems] = useState<Movement[]>([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totals, setTotals] = useState<{ cobros: number; gastos: number; balance: number }>({
    cobros: 0,
    gastos: 0,
    balance: 0,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Movement | null>(null);
  const [deleting, setDeleting] = useState<Movement | null>(null);
  const [editBalance, setEditBalance] = useState(false);
  const [balanceInput, setBalanceInput] = useState(0);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Running bank balance after each movement, in display order. The list is shown
  // newest-first, so we accumulate from the bottom (oldest) up; the top row then
  // equals the current "Saldo en banco". Computed, never stored, so it stays right
  // after any add/edit/delete/reorder.
  const balanceById = useMemo(() => {
    const map = new Map<string, number>();
    let bal = openingBalance;
    for (let i = items.length - 1; i >= 0; i--) {
      const m = items[i];
      if (!m) continue;
      const amt = Number.parseFloat(m.amount);
      bal += m.type === 'cobro' ? amt : -amt;
      map.set(m.id, bal);
    }
    return map;
  }, [items, openingBalance]);

  const loadAll = useCallback(async () => {
    try {
      const res = await api<PersonalResponse>('/admin/personal');
      setItems(res.items);
      setOpeningBalance(res.openingBalance);
      setTotals(res.totals);
    } catch (e) {
      onError(e);
    }
  }, [onError]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  async function handleDelete(id: string) {
    try {
      await api(`/admin/personal/${id}`, { method: 'DELETE' });
      onToast('Movimiento borrado', 'ok');
      setDeleting(null);
      void loadAll();
    } catch (e) {
      onError(e);
    }
  }

  async function saveBalance() {
    try {
      await api('/admin/personal/opening-balance', {
        method: 'PUT',
        body: { openingBalance: balanceInput },
      });
      onToast('Saldo inicial actualizado', 'ok');
      setEditBalance(false);
      void loadAll();
    } catch (e) {
      onError(e);
    }
  }

  // Drag & drop reordering, confined to a single day. Reorders the list locally
  // (optimistic) and persists that day's new sequence; on error, reload to revert.
  function reorder(fromId: string, toId: string) {
    if (fromId === toId) return;
    const from = items.findIndex((m) => m.id === fromId);
    const to = items.findIndex((m) => m.id === toId);
    if (from < 0 || to < 0) return;
    const moved = items[from];
    const target = items[to];
    if (!moved || !target || moved.date !== target.date) return; // never cross days
    const next = [...items];
    next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    const dayIds = next.filter((m) => m.date === moved.date).map((m) => m.id);
    void persistOrder(moved.date, dayIds);
  }

  async function persistOrder(date: string, orderedIds: string[]) {
    try {
      await api('/admin/personal/reorder', { method: 'PUT', body: { date, orderedIds } });
    } catch (e) {
      onError(e);
      void loadAll();
    }
  }

  return (
    <div className="body">
      <div className="pageh">
        <h1>
          Personal <span className="v2-pill">privado</span>
        </h1>
        <div className="sub">tus gastos y cobros · solo tú ves esto</div>
      </div>

      <div className="kpis">
        <div className="kpi k4">
          <div className="lbl">Saldo en banco</div>
          <div
            className="v"
            style={{ color: totals.balance >= 0 ? 'var(--accent-text)' : 'var(--red-text)' }}
          >
            {fmt(totals.balance)}
          </div>
          <div className="s">
            inicial {fmt(openingBalance)} · cobros +{fmt(totals.cobros)} · gastos −{fmt(totals.gastos)}
          </div>
        </div>
        <div className="kpi k2">
          <div className="lbl">Total cobrado</div>
          <div className="v" style={{ color: 'var(--accent-text)' }}>
            +{fmt(totals.cobros)}
          </div>
          <div className="s">suma de todos los cobros</div>
        </div>
        <div className="kpi k3">
          <div className="lbl">Total gastado</div>
          <div className="v" style={{ color: 'var(--red-text)' }}>
            −{fmt(totals.gastos)}
          </div>
          <div className="s">suma de todos los gastos</div>
        </div>
        <div className="kpi k1">
          <div className="lbl">Saldo inicial</div>
          <div className="v">{fmt(openingBalance)}</div>
          <div className="s">
            <button
              className="btn ghost"
              style={{ padding: '2px 8px', fontSize: 11 }}
              onClick={() => {
                setBalanceInput(openingBalance);
                setEditBalance(true);
              }}
            >
              Editar
            </button>
          </div>
        </div>
      </div>

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Movimientos</h3>
          <span
            style={{ color: 'var(--muted)', fontFamily: 'Geist Mono, monospace', fontSize: 11 }}
          >
            {items.length} movimiento{items.length === 1 ? '' : 's'}
          </span>
          <div className="meta" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn primary" onClick={() => setShowCreate(true)}>
              <span className="plus">+</span> Añadir movimiento
            </button>
          </div>
        </div>
        <table className="t">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Concepto</th>
              <th className="num">Importe</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr
                key={m.id}
                onDragOver={(e) => {
                  if (!dragId) return;
                  const dragged = items.find((x) => x.id === dragId);
                  if (!dragged || dragged.date !== m.date) return; // only within the same day
                  e.preventDefault();
                  if (dragOverId !== m.id) setDragOverId(m.id);
                }}
                onDrop={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  reorder(dragId, m.id);
                  setDragId(null);
                  setDragOverId(null);
                }}
                style={
                  dragOverId === m.id ? { boxShadow: 'inset 0 2px 0 0 var(--accent-text)' } : undefined
                }
              >
                <td data-label="">
                  <span
                    className="icon-btn"
                    role="button"
                    aria-label="Arrastrar para reordenar"
                    title="Arrastra para reordenar (dentro del mismo día)"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      setDragId(m.id);
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setDragOverId(null);
                    }}
                    style={{ cursor: 'grab', color: 'var(--muted)', display: 'inline-flex' }}
                  >
                    <GripIcon />
                  </span>
                </td>
                <td className="mono" data-label="Fecha">
                  {fmtDate(m.date)}
                </td>
                <td data-label="Tipo">
                  <span
                    className="aging-chip"
                    style={{
                      background: m.type === 'cobro' ? 'var(--accent-bg)' : 'rgba(168, 53, 26, 0.12)',
                      color: m.type === 'cobro' ? 'var(--accent-text)' : 'var(--red-text)',
                    }}
                  >
                    {m.type}
                  </span>
                </td>
                <td
                  style={{ color: m.concept ? 'var(--ink)' : 'var(--muted)' }}
                  data-label="Concepto"
                >
                  {m.concept ?? '—'}
                </td>
                <td className="num" data-label="Importe">
                  <span
                    style={{
                      color: m.type === 'cobro' ? 'var(--accent-text)' : 'var(--red-text)',
                      fontWeight: 600,
                    }}
                  >
                    {m.type === 'cobro' ? '+' : '−'}
                    {fmt(m.amount)}
                  </span>
                  <div
                    style={{
                      color: 'var(--muted)',
                      fontFamily: 'Geist Mono, monospace',
                      fontSize: 10.5,
                      marginTop: 2,
                    }}
                    title="Saldo tras esta operación"
                  >
                    {fmt(balanceById.get(m.id) ?? 0)}
                  </div>
                </td>
                <td data-label="">
                  <div className="row-actions">
                    <button
                      className="icon-btn"
                      title="Editar movimiento"
                      aria-label="Editar movimiento"
                      onClick={() => setEditing(m)}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="icon-btn danger"
                      title="Borrar movimiento"
                      aria-label="Borrar movimiento"
                      onClick={() => setDeleting(m)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="empty">Sin movimientos. Añade tu primer gasto o cobro.</div>
        )}
      </section>

      {(showCreate || editing) && (
        <MovementModal
          initial={editing ?? undefined}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={(action) => {
            setShowCreate(false);
            setEditing(null);
            onToast(action === 'created' ? 'Movimiento añadido' : 'Movimiento actualizado', 'ok');
            void loadAll();
          }}
          onError={onError}
        />
      )}

      {editBalance && (
        <div className="modal-backdrop" onClick={() => setEditBalance(false)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              void saveBalance();
            }}
          >
            <h3>Saldo inicial</h3>
            <div className="field">
              <label>Saldo de partida en banco (€)</label>
              <input
                type="number"
                step="0.01"
                value={balanceInput}
                onChange={(e) => setBalanceInput(Number.parseFloat(e.target.value) || 0)}
                autoFocus
              />
            </div>
            <div
              style={{
                fontFamily: 'Geist Mono, monospace',
                fontSize: 10.5,
                color: 'var(--muted)',
                lineHeight: 1.5,
              }}
            >
              El saldo en banco se calcula como saldo inicial + cobros − gastos.
            </div>
            <div className="actions">
              <button type="button" className="btn ghost" onClick={() => setEditBalance(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn primary">
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <ConfirmDialog
          tone="danger"
          title="Borrar movimiento"
          message={`Se borrará el ${deleting.type} de ${fmt(deleting.amount)} del ${fmtDate(deleting.date)}.`}
          confirmLabel="Borrar"
          onCancel={() => setDeleting(null)}
          onConfirm={() => handleDelete(deleting.id)}
        />
      )}
    </div>
  );
}

function MovementModal(props: {
  initial?: Movement;
  onClose: () => void;
  onSaved: (action: 'created' | 'updated') => void;
  onError: (e: unknown) => void;
}) {
  const isEdit = !!props.initial;
  const [type, setType] = useState<MovementType>(props.initial?.type ?? 'gasto');
  const [amount, setAmount] = useState<number>(
    props.initial ? Number.parseFloat(props.initial.amount) : 0,
  );
  const [date, setDate] = useState(props.initial?.date ?? todayIso());
  const [concept, setConcept] = useState(props.initial?.concept ?? '');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (amount <= 0) {
      props.onError(new Error('El importe debe ser mayor que 0'));
      return;
    }
    setBusy(true);
    try {
      const body = { type, amount, date, concept: concept.trim() || null };
      if (isEdit && props.initial) {
        await api(`/admin/personal/${props.initial.id}`, { method: 'PATCH', body });
        props.onSaved('updated');
      } else {
        await api('/admin/personal', { method: 'POST', body });
        props.onSaved('created');
      }
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{isEdit ? 'Editar movimiento' : 'Añadir movimiento'}</h3>
        <div className="field">
          <label>Tipo</label>
          <select value={type} onChange={(e) => setType(e.target.value as MovementType)}>
            <option value="gasto">Gasto (resta al saldo)</option>
            <option value="cobro">Cobro (suma al saldo)</option>
          </select>
        </div>
        <div className="field">
          <label>Importe (€)</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={amount}
            onChange={(e) => setAmount(Number.parseFloat(e.target.value) || 0)}
            required
          />
        </div>
        <div className="field">
          <label>Fecha</label>
          <DateInput value={date} onChange={setDate} required />
        </div>
        <div className="field">
          <label>Concepto</label>
          <input
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            maxLength={500}
            placeholder="opcional"
          />
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn primary" disabled={busy || amount <= 0}>
            {isEdit ? 'Guardar' : 'Añadir'}
          </button>
        </div>
      </form>
    </div>
  );
}
